/**
 * №13.1 §6 (§3.4) — the ONE time-unit formatter, system-wide.
 *
 * A time entry stores `units` as a **raw number** — a count of 6-minute units.
 * The firm setting `commercial.time_unit` picks how that raw number is *shown*;
 * the stored value never changes (N14: derive, don't store). One hour = 10 units,
 * so hrs = rawUnits × 6/60 = rawUnits / 10.
 *
 *   six_minute    → "N units (= H.h hrs)"   (e.g. 25 → "25 units (= 2.5 hrs)")
 *   decimal_hours → "H.h hrs"               (e.g. 25 → "2.5 hrs")
 *
 * Every units-display site (day view, matter capture, WIP worksheet, invoice
 * lines) calls `formatUnits` so no surface formats differently (M27/M29/N15).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { mcpClient } from "../../lib/mcp-client";

export type TimeUnitMode = "six_minute" | "decimal_hours";

export const TIME_UNIT_KEY = "commercial.time_unit";
export const DEFAULT_TIME_UNIT_MODE: TimeUnitMode = "six_minute";

/** 1 hour = 10 six-minute units. */
const UNITS_PER_HOUR = 10;

/** Format a raw unit number to a fixed 1-decimal hours string. */
function hoursOf(rawUnits: number): string {
  const n = Number.isFinite(rawUnits) ? rawUnits : 0;
  return (n / UNITS_PER_HOUR).toFixed(1);
}

/** Render the raw unit count without trailing noise (integers stay integers). */
function unitCountOf(rawUnits: number): string {
  const n = Number.isFinite(rawUnits) ? rawUnits : 0;
  return Number.isInteger(n) ? String(n) : String(Math.round(n * 100) / 100);
}

/**
 * The single display formatter. Pure — no stored strings, no side effects.
 */
export function formatUnits(rawUnits: number, mode: TimeUnitMode): string {
  const hrs = hoursOf(rawUnits);
  if (mode === "decimal_hours") return `${hrs} hrs`;
  return `${unitCountOf(rawUnits)} units (= ${hrs} hrs)`;
}

/**
 * Parse a units entry typed in the current mode back to raw units (the stored
 * shape). In `six_minute` the user types unit counts directly; in
 * `decimal_hours` they type hours, which convert to raw units (× 10). Mirror of
 * `formatUnits` so entry parses the same way it displays (M29).
 */
export function parseUnitsInput(input: string, mode: TimeUnitMode): number {
  const v = parseFloat(input);
  if (!Number.isFinite(v)) return 0;
  return mode === "decimal_hours" ? Math.round(v * UNITS_PER_HOUR) : v;
}

// ---------------------------------------------------------------------------
// Mode read/write — one workspace-context key, read once per surface. Kept in
// this owned module (not a shared hook file) so every units surface shares the
// same cached read and the same key.
// ---------------------------------------------------------------------------

const timeUnitQueryKey = [TIME_UNIT_KEY] as const;

/** Read `commercial.time_unit`; defaults to six-minute when unset. */
export function useTimeUnitMode() {
  const query = useQuery({
    queryKey: timeUnitQueryKey,
    queryFn: async (): Promise<TimeUnitMode> => {
      const v = await mcpClient.getWorkspaceContext(TIME_UNIT_KEY);
      return v === "decimal_hours" ? "decimal_hours" : DEFAULT_TIME_UNIT_MODE;
    },
    staleTime: 60_000,
  });
  return { ...query, mode: query.data ?? DEFAULT_TIME_UNIT_MODE };
}

/** Write the firm's time-capture unit via the Stage-A static tool. */
export function useSetTimeUnitMode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (mode: TimeUnitMode) => {
      const r = await mcpClient.toolsCall("update_workspace_settings", { time_unit: mode });
      if (r.isError) throw new Error(r.content?.[0]?.text ?? "update_workspace_settings failed");
      return mode;
    },
    onSuccess: (mode) => {
      qc.setQueryData(timeUnitQueryKey, mode);
      void qc.invalidateQueries({ queryKey: timeUnitQueryKey });
    },
  });
}
